import React, { useState, useEffect, useCallback, useRef } from "react";
import { 
  Box, Typography, IconButton, InputBase, Avatar, Badge, Menu, MenuItem, 
  Divider, Tooltip, CircularProgress, Drawer, Chip, TextField, Button,
  List, ListItemButton, ListItemAvatar, ListItemText,
  Dialog, DialogTitle, DialogContent, DialogActions
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendIcon from "@mui/icons-material/Send";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import SchoolIcon from "@mui/icons-material/School";
import ComputerIcon from "@mui/icons-material/Computer";
import PublicIcon from "@mui/icons-material/Public";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import CloseIcon from "@mui/icons-material/Close";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import InsertEmoticonIcon from "@mui/icons-material/InsertEmoticon";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckIcon from "@mui/icons-material/Check";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { Alert, Snackbar } from "@mui/material";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EmojiPicker, { EmojiStyle } from 'emoji-picker-react';
import axios from "axios";
import { useAuth } from "../../../../context/AuthContext";
import { useSystems } from "../../../../context/SystemContext";
import SharedPostCard, { isPostPayload } from "../post/SharedPostCard";

const API_BASE = "https://apiweb.hitpoly.com/ajax/chatCRMController.php";

const originIcons = {
  bolsa: <BusinessCenterIcon sx={{ fontSize: 14 }} />,
  academia: <SchoolIcon sx={{ fontSize: 14 }} />,
  crm: <ComputerIcon sx={{ fontSize: 14 }} />,
  comunidad: <PublicIcon sx={{ fontSize: 14 }} />
};

const originColors = {
  bolsa: '#3B207F',
  academia: '#6C4DE2',
  crm: '#1877F2',
  comunidad: '#0a66c2'
};

export default function GlobalMessenger() {
  const { user } = useAuth();
  const { activeProfile } = useSystems();
  const [selectedChat, setSelectedChat] = useState(null);
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [crmOpen, setCrmOpen] = useState(false);
  
  const [activeContext, setActiveContext] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [messages, setMessages] = useState([]);
  
  const [stages, setStages] = useState([]);
  const [isArchivedView, setIsArchivedView] = useState(false);
  const [newTagInput, setNewTagInput] = useState("");
  const [saving, setSaving] = useState(false);
  const [funnelDialogOpen, setFunnelDialogOpen] = useState(false);
  const [newFunnelName, setNewFunnelName] = useState("");
  const [newFunnelStages, setNewFunnelStages] = useState(["Lead", "Contactado", "Interesado", "Cierre"]);
  const [isCreatingStage, setIsCreatingStage] = useState(false);
  const [tempStageName, setTempStageName] = useState("");
  const [stageFilter, setStageFilter] = useState(null);
  const stagesOpeningRef = useRef(false);
  const [funnelFilter, setFunnelFilter] = useState(() => {
    const saved = localStorage.getItem('messenger_active_funnel');
    if (saved === 'all') return 'all';
    return saved ? parseInt(saved) : null;
  });
  const [funnelFilterAnchorEl, setFunnelFilterAnchorEl] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [dialogStageInput, setDialogStageInput] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [chatMenuTarget, setChatMenuTarget] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [chatStageAnchorEl, setChatStageAnchorEl] = useState(null);
  const [quillRef, setQuillRef] = useState(null);
  const [selectedFunnelId, setSelectedFunnelId] = useState(null);
  const [notes, setNotes] = useState([]);
  const [newNoteInput, setNewNoteInput] = useState("");
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const typingTimeoutRef = useRef(null);
  const emojiRef = useRef(null);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const userScrolledUpRef = useRef(false);

  // Auto-scroll al fondo SOLO si el usuario está cerca del fondo o cambió de chat
  useEffect(() => {
    if (!scrollContainerRef.current) return;
    const el = scrollContainerRef.current;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
    if (!userScrolledUpRef.current || nearBottom) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    }
  }, [messages, selectedChat, otherUserTyping]);

  // Cerrar emoji picker al hacer clic fuera
  useEffect(() => {
    if (!emojiPickerOpen) return;
    const handleClickOutside = (e) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target)) {
        setEmojiPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [emojiPickerOpen]);

  const prevContextIdRef = useRef(null);

  useEffect(() => {
    if (activeProfile?.id) {
      if (prevContextIdRef.current !== null && prevContextIdRef.current !== activeProfile.id) {
        setSelectedChat(null);
        setMessages([]);
        setNewMessage("");
      }
      prevContextIdRef.current = activeProfile.id;
      setActiveContext(activeProfile);
    }
  }, [activeProfile]);
  useEffect(() => {
    if (selectedChat?.id) {
      fetchNotes(selectedChat.id);
    }
  }, [selectedChat?.id]);

  const fetchNotes = async (conversationId) => {
    try {
      const res = await axios.post(API_BASE, { accion: 'getNotes', conversation_id: conversationId });
      if (res.data.success) setNotes(res.data.data);
    } catch (e) { }
  };

  const handleAddNote = async () => {
    if (!newNoteInput.trim() || !selectedChat) return;
    try {
      const res = await axios.post(API_BASE, { 
        accion: 'addNote', 
        conversation_id: selectedChat.id, 
        user_id: user.id || user.user_id,
        content: newNoteInput 
      });
      if (res.data.success) {
        setNewNoteInput("");
        fetchNotes(selectedChat.id);
      }
    } catch (e) { }
  };

  // 1.1 CARGAR ETAPAS DEL EMBUDO
  useEffect(() => {
    const fetchStages = async () => {
      try {
        const res = await axios.post(API_BASE, { accion: 'getFunnelStages' });
        if (res.data.success) {
          setStages(res.data.data);
          
          // Lógica de primer inicio mejorada
          const saved = localStorage.getItem('messenger_active_funnel');
          if (!saved && !funnelFilter && res.data.data.length > 0) {
            const firstFunnelId = res.data.data[0].funnel_id;
            setFunnelFilter(firstFunnelId);
            localStorage.setItem('messenger_active_funnel', firstFunnelId);
          }
        }
      } catch (e) { }
    };
    fetchStages();
  }, [funnelFilter]);

  // 2. CARGAR CONVERSACIONES DEL CONTEXTO SELECCIONADO
  const fetchConversations = useCallback(async (showLoading = true) => {
    if (!activeContext) return;
    if (showLoading) setLoading(true);
    try {
      const res = await axios.post(API_BASE, { 
        accion: 'getConversations', 
        profile_id: activeContext.id,
        profile_type: activeContext.type,
        context_id: activeContext.id,
        context_type: activeContext.type,
        is_archived: isArchivedView ? 1 : 0
      });
      if (res.data.success) {
        setConversations(res.data.data);
        return res.data.data;
      }
    } catch (e) { }
    finally { setLoading(false); }
  }, [activeContext, isArchivedView]);

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Escuchar actividad en chats desde burbujas u otras partes para actualizar la UI en tiempo real
  useEffect(() => {
    const handleChatActivity = (event) => {
      const { conversationId } = event.detail || {};
      if (!conversationId) return;

      // Actualizar la lista de conversaciones sin molestar al usuario con el spinner
      fetchConversations(false);

      // Si el chat actualmente seleccionado coincide, actualizar sus mensajes
      if (selectedChat && String(selectedChat.id) === String(conversationId)) {
        const loadMessages = async (chat) => {
          if (!chat || !activeContext) return;
          try {
            const other_id = (chat.user_1_id === activeContext.id) ? chat.user_2_id : chat.user_1_id;
            const res = await axios.post(API_BASE, { 
              accion: 'getMessages', 
              conversation_id: chat.id 
            });
            if (res.data.success) {
              setMessages(res.data.data);
            }
          } catch (error) { }
        };
        loadMessages(selectedChat);
      }
    };

    window.addEventListener('chat-activity', handleChatActivity);
    return () => window.removeEventListener('chat-activity', handleChatActivity);
  }, [fetchConversations, selectedChat, activeContext]);

  // 2.1 AUTO-SELECCIONAR CHAT POR URL PARAM (Deep Linking) O LOCALSTORAGE
  useEffect(() => {
    if (conversations.length > 0 && !selectedChat && activeContext) {
      // Prioridad 1: URL param (Deep Linking)
      const params = new URLSearchParams(window.location.search);
      const targetUserId = params.get('userId');
      if (targetUserId) {
        const chat = conversations.find(c => 
          String(c.user_1_id) === String(targetUserId) || 
          String(c.user_2_id) === String(targetUserId) ||
          String(c.other_user_id) === String(targetUserId)
        );
        if (chat) {
          setSelectedChat(chat);
          return;
        }
      }

      // Prioridad 2: LocalStorage
      const savedChatId = localStorage.getItem(`messenger_selected_chat_id_${activeContext.id}`);
      if (savedChatId) {
        const chat = conversations.find(c => String(c.id) === String(savedChatId));
        if (chat) {
          setSelectedChat(chat);
        }
      }
    }
  }, [conversations, selectedChat, activeContext]);

  // Guardar el chat seleccionado en localStorage
  useEffect(() => {
    if (activeContext) {
      if (selectedChat) {
        localStorage.setItem(`messenger_selected_chat_id_${activeContext.id}`, selectedChat.id);
      } else {
        localStorage.removeItem(`messenger_selected_chat_id_${activeContext.id}`);
      }
    }
  }, [selectedChat, activeContext]);

  // HEARTBEAT: Actualizar actividad para estar "Online"
  useEffect(() => {
    if (!user) return;
    
    const updatePresence = async () => {
      try {
        if (!user) return;
        const uid = user.id || user.user_id || user.uid || user.sub;
        
        if (!uid) {
            return;
        }

        await axios.post(API_BASE, { 
          accion: 'updateActivity', 
          user_id: uid 
        });
      } catch (e) {
      }
    };

    updatePresence();
    const interval = setInterval(updatePresence, 30000); // Cada 30 seg
    return () => clearInterval(interval);
  }, [user]);

  // POLLING: Estado de "Escribiendo" del otro usuario
  useEffect(() => {
    if (!selectedChat || !user) {
      setOtherUserTyping(false);
      return;
    }

    const checkTyping = async () => {
      try {
        const res = await axios.post(API_BASE, { 
          accion: 'getTypingStatus', 
          conversation_id: selectedChat.id,
          user_id: user.id
        });
        if (res.data.success) {
          setOtherUserTyping(!!res.data.typing_user_id);
        }
      } catch (e) {}
    };

    const interval = setInterval(checkTyping, 5000); // Cada 5 seg
    return () => clearInterval(interval);
  }, [selectedChat, user]);

  // Manejar mi propio estado de "Escribiendo"
  const handleMyTyping = useCallback(async (isTyping) => {
    if (!selectedChat || !user) return;
    try {
      await axios.post(API_BASE, {
        accion: 'setTyping',
        conversation_id: selectedChat.id,
        user_id: user.id,
        is_typing: isTyping
      });
    } catch (e) {}
  }, [selectedChat, user]);

  const onEditorChange = (content) => {
    setNewMessage(content);
    
    // Notificar que estoy escribiendo
    handleMyTyping(true);
    
    // Timer para dejar de escribir tras 3 segundos de inactividad
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      handleMyTyping(false);
    }, 3000);
  };

  // 3. CARGAR MENSAJES CUANDO SE SELECCIONA UN CHAT
  useEffect(() => {
    if (!selectedChat) return;
    const loadMessages = async (chat) => {
      if (!chat || !activeContext) return;
      try {
        // Refrescar embudos/etapas para ver los recién creados
        try {
          const resF = await axios.post(API_BASE, { accion: 'getFunnelStages' });
          if (resF.data.success && resF.data.data.length) setStages(resF.data.data);
        } catch (e) {}
        const other_id = (chat.user_1_id === activeContext.id) ? chat.user_2_id : chat.user_1_id;
        const res = await axios.post(API_BASE, { 
          accion: 'getMessages', 
          conversation_id: chat.id 
        });
        if (res.data.success) {
          setMessages(res.data.data);
          // Marcar como leídos
          axios.post(API_BASE, { 
            accion: 'markAsRead', 
            user_a: activeContext.id,
            user_b: other_id
          });
        }
      } catch (error) { }
    };
    loadMessages(selectedChat);

    // Al seleccionar chat, sincronizar el embudo seleccionado en el panel CRM
    if (selectedChat?.funnel_stage_id) {
      const stage = stages.find(s => s.id == selectedChat.funnel_stage_id);
      if (stage) setSelectedFunnelId(stage.funnel_id);
    } else if (!selectedFunnelId && stages.length > 0) {
      setSelectedFunnelId(stages[0].funnel_id);
    }
  }, [selectedChat, activeContext, stages, selectedFunnelId]);


  // 4. ENVIAR MENSAJE
  const handleSendMessage = async () => {
    const plainText = newMessage.replace(/<[^>]*>?/gm, '').trim();
    
    if (!plainText && !newMessage.includes('<img')) {
       return;
    }

    if (!selectedChat || !activeContext) {
      return;
    }

    const content = newMessage;
    const tempId = `temp-${Date.now()}`;
    
    // 1. Update messages optimistically
    const optimisticMsg = {
      id: tempId,
      sender_id: activeContext.id,
      message_text: content,
      created_at: new Date().toISOString(),
      is_optimistic: true
    };
    setMessages(prev => [...prev, optimisticMsg]);
    setNewMessage('');
    
    // 2. Update conversation list optimistically
    setConversations(prev => {
      const filtered = prev.filter(c => c.id !== selectedChat.id);
      const updatedChat = { 
        ...selectedChat, 
        last_message: content, 
        last_message_time: new Date().toISOString(),
        unread_count: 0 // Resetting since we are the sender
      };
      return [updatedChat, ...filtered];
    });

    try {
      const res = await axios.post(API_BASE, {
        accion: 'sendMessage',
        conversation_id: selectedChat.id,
        sender_id: activeContext.id,
        sender_profile_id: activeContext.id,
        sender_profile_type: activeContext.type,
        text: content
      });
      
      if (res.data.success) {
        // Disparar evento para actualizar componentes y burbujas activas
        window.dispatchEvent(new CustomEvent('chat-activity', {
          detail: { conversationId: selectedChat.id }
        }));

        // Fetch real messages to sync IDs and timestamps
        const resMsg = await axios.post(API_BASE, { 
          accion: 'getMessages', 
          conversation_id: selectedChat.id 
        });
        if (resMsg.data.success) setMessages(resMsg.data.data);
        
        // Refresh conversations in background
        fetchConversations(false);
      } else {
        // Rollback on failure
        setMessages(prev => prev.filter(m => m.id !== tempId));
        setNewMessage(content);
        setSnackbar({ open: true, message: "Error al enviar mensaje", severity: "error" });
      }
    } catch (e) { 
      setMessages(prev => prev.filter(m => m.id !== tempId));
      setNewMessage(content);
      setSnackbar({ open: true, message: "Error de conexión", severity: "error" });
    }
  };

  // 5. ACCIONES CRM
  const handleUpdateStage = async (stageId) => {
    if (!selectedChat) return;
    try {
      const res = await axios.post(API_BASE, {
        accion: 'updateFunnelStage',
        conversation_id: selectedChat.id,
        stage_id: stageId
      });
      if (res.data.success) {
        const stageObj = stages.find(s => s.id == stageId);
        const stageName = stageObj?.name || 'Sin Etapa';
        const stageColor = stageObj?.color || '#888';

        // 1. Actualizar chat seleccionado
        setSelectedChat(prev => ({ 
          ...prev, 
          funnel_stage_id: stageId, 
          stage_name: stageName, 
          stage_color: stageColor,
          funnel_id: stageObj?.funnel_id || prev.funnel_id
        }));
        
        // 2. Actualizar lista de conversaciones localmente
        setConversations(prev => prev.map(c => 
          c.id === selectedChat.id ? { 
            ...c, 
            funnel_stage_id: stageId, 
            stage_name: stageName, 
            stage_color: stageColor,
            funnel_id: stageObj?.funnel_id || c.funnel_id
          } : c
        ));

        fetchConversations(false);
      }
    } catch (e) { }
  };

  const handleUpdateFunnel = async (funnelId) => {
    if (!selectedChat) return;
    setSelectedFunnelId(funnelId);
    // Buscamos la primera etapa de ese embudo para asignarla por defecto
    const firstStage = stages.find(s => s.funnel_id == funnelId);
    if (firstStage) {
      handleUpdateStage(firstStage.id);
    }
  };

  const handleArchive = async () => {
    if (!selectedChat) return;
    try {
      const res = await axios.post(API_BASE, {
        accion: 'archiveConversation',
        conversation_id: selectedChat.id,
        is_archived: isArchivedView ? 0 : 1
      });
      if (res.data.success) {
        setSelectedChat(null);
        fetchConversations(false);
      }
    } catch (e) { }
  };

  const handleOpenChatMenu = (e, chat) => {
    e.stopPropagation();
    setChatMenuTarget(chat);
    setChatMenuAnchor(e.currentTarget);
  };

  const handleDeleteConversation = async () => {
    if (!chatMenuTarget) return;
    setChatMenuAnchor(null);
    const deletedId = chatMenuTarget.id;
    const wasSelected = selectedChat?.id === deletedId;
    try {
      const res = await axios.post(API_BASE, {
        accion: 'deleteConversation',
        conversation_id: deletedId
      });
      if (res.data.success) {
        const newConvs = await fetchConversations(false);
        if (wasSelected && newConvs && newConvs.length > 0) {
          const next = newConvs.find(c => c.id !== deletedId) || newConvs[0];
          setSelectedChat(next);
        }
        setChatMenuTarget(null);
      }
    } catch (e) { }
  };

  const openDeleteConfirm = (e) => {
    setChatMenuAnchor(null);
    setConfirmDialog({
      open: true,
      title: 'Eliminar conversación',
      message: '¿Estás seguro de eliminar esta conversación?',
      onConfirm: () => {
        setConfirmDialog(prev => ({ ...prev, open: false }));
        handleDeleteConversation();
      }
    });
  };

  const handleCreateFunnel = async () => {
    if (!newFunnelName.trim()) return;
    try {
      const res = await axios.post(API_BASE, { 
        accion: 'createFunnel', 
        name: newFunnelName,
        owner_id: activeContext?.id,
        project: selectedChat?.project_source || 'bolsa'
      });

      if (res.data.success) {
        const funnelId = res.data.id;
        for (const sName of newFunnelStages) {
          await axios.post(API_BASE, { accion: 'createFunnelStage', name: sName, funnel_id: funnelId });
        }
        setFunnelDialogOpen(false);
        setNewFunnelName("");
        setNewFunnelStages(["Lead", "Contactado", "Interesado", "Cierre"]);
        
        const resS = await axios.post(API_BASE, { accion: 'getFunnelStages' });
        if (resS.data.success) {
          setStages(resS.data.data);
          setFunnelFilter(funnelId);
          localStorage.setItem('messenger_active_funnel', funnelId);
          setSnackbar({ open: true, message: "¡Embudo creado con éxito!", severity: "success" });
        }
      } else {
        setSnackbar({ open: true, message: "Error: " + (res.data.message || "No se pudo crear"), severity: "error" });
      }
    } catch (e) { 
      setSnackbar({ open: true, message: "Error crítico al crear embudo", severity: "error" });
    }
  };

  const handleCreateStage = async () => {
    const name = prompt("Nombre de la nueva etapa:");
    if (!name) return;
    try {
      const res = await axios.post(API_BASE, { accion: 'createFunnelStage', name });
      if (res.data.success) {
        const resS = await axios.post(API_BASE, { accion: 'getFunnelStages' });
        if (resS.data.success) setStages(resS.data.data);
      }
    } catch (e) { }
  };

  const handleDeleteStage = async (e, stageId) => {
    e.stopPropagation(); // Evitar que se seleccione la etapa al querer borrarla
    if (!window.confirm("¿Seguro que quieres eliminar esta etapa?")) return;
    try {
      const res = await axios.post(API_BASE, { accion: 'deleteFunnelStage', stage_id: stageId });
      if (res.data.success) {
        const resS = await axios.post(API_BASE, { accion: 'getFunnelStages' });
        if (resS.data.success) setStages(resS.data.data);
      }
    } catch (e) { }
  };

  const handleEmojiClick = (emojiData) => {
    if (quillRef) {
      const quill = quillRef.getEditor();
      const range = quill.getSelection(true);
      quill.insertText(range.index, emojiData.emoji);
      quill.setSelection(range.index + emojiData.emoji.length);
    } else {
      setNewMessage(prev => prev + emojiData.emoji);
    }
  };

  const handleSaveNewStage = async () => {
    if (!tempStageName.trim()) {
      setIsCreatingStage(false);
      return;
    }
    try {
      const res = await axios.post(API_BASE, { 
        accion: 'createFunnelStage', 
        name: tempStageName,
        funnel_id: stages[0]?.funnel_id || 1 // Usar el embudo actual o el 1
      });
      if (res.data.success) {
        const resS = await axios.post(API_BASE, { accion: 'getFunnelStages' });
        if (resS.data.success) {
          const newStages = resS.data.data;
          setStages(newStages);
          // Buscar la etapa recién creada (probablemente la última por ID)
          const latestStage = [...newStages].sort((a,b) => b.id - a.id)[0];
          if (latestStage) handleUpdateStage(latestStage.id);
        }
      }
    } catch (e) { }
    setIsCreatingStage(false);
    setTempStageName("");
  };

  const handleAddTag = async () => {
    if (!newTagInput.trim() || !selectedChat) return;
    try {
      const res = await axios.post(API_BASE, {
        accion: 'addTag',
        conversation_id: selectedChat.id,
        tag_name: newTagInput,
        owner_id: activeContext?.id
      });
      if (res.data.success) {
        // Creamos un objeto de tag temporal para la UI (el backend ya lo creó)
        const newTag = { 
          id: Date.now(), // ID temporal
          name: newTagInput, 
          color: '#3B207F' 
        };

        // 1. Actualizar el chat seleccionado inmediatamente
        setSelectedChat(prev => ({
          ...prev,
          tags: [...(prev.tags || []), newTag]
        }));

        // 2. Actualizar la lista de conversaciones para que se vea en el sidebar
        setConversations(prev => prev.map(c => 
          c.id === selectedChat.id 
            ? { ...c, tags: [...(c.tags || []), newTag] } 
            : c
        ));

        setNewTagInput("");
        
        // 3. Sincronización real de fondo para obtener IDs y colores finales de DB
        setTimeout(() => fetchConversations(false), 1000);
      }
    } catch (e) { }
  };

  const handleRemoveTag = async (tagId) => {
    if (!selectedChat) return;
    try {
      const res = await axios.post(API_BASE, {
        accion: 'removeTag',
        conversation_id: selectedChat.id,
        tag_id: tagId
      });
      if (res.data.success) {
        setSelectedChat(prev => ({ ...prev, tags: prev.tags.filter(t => t.id !== tagId) }));
        fetchConversations(false);
      }
    } catch (e) { }
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      height: 'calc(100vh - 64px)', 
      width: '100%', 
      bgcolor: '#f0f2f5', 
      overflow: 'hidden', 
      fontFamily: '"Inter", "Roboto", sans-serif',
      position: 'relative'
    }}>
      
      {/* LEFT SIDEBAR (CHATS LIST) */}
      <Box sx={{ width: { xs: '100%', md: '420px' }, height: '100%', bgcolor: '#fff', borderRight: '1px solid #eef0f2', display: { xs: selectedChat ? 'none' : 'flex', md: 'flex' }, flexDirection: 'column', transition: 'all 0.3s ease' }}>
        
        {/* Header */}
        <Box sx={{ p: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #f0f2f5' }}>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            {/* NUEVO: Filtro por Embudo (PERSISTENTE) */}
            <Tooltip title={stages.find(s => s.funnel_id === funnelFilter)?.funnel_name || "Seleccionar Embudo"} placement="bottom">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  setFunnelFilterAnchorEl(e.currentTarget);
                }}
                sx={{ 
                  color: '#3B207F', 
                  bgcolor: '#3B207F15', 
                  borderRadius: '10px',
                  '&:hover': { bgcolor: '#3B207F25' }
                }}
              >
                <AccountTreeIcon fontSize="small" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Filtrar por Etapa de este Embudo">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  setFilterAnchorEl(e.currentTarget);
                }} 
                sx={{ 
                  color: stageFilter ? '#3B207F' : '#5f6368', 
                  bgcolor: stageFilter ? '#3B207F15' : '#f8f9fa', 
                  borderRadius: '10px' 
                }}
              >
                <FilterListIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>

          <Menu 
            anchorEl={filterAnchorEl} 
            open={Boolean(filterAnchorEl)} 
            onClose={() => setFilterAnchorEl(null)} 
            sx={{ zIndex: 110000 }}
            PaperProps={{ 
              sx: { 
                borderRadius: 3, 
                mt: 1, 
                boxShadow: '0 10px 40px rgba(0,0,0,0.1)', 
                border: '1px solid #f0f2f5', 
                minWidth: 200,
                zIndex: 110001,
                maxHeight: 280, // Altura para aprox 5-6 items total
                overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '3px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { 
                  background: 'transparent', 
                  borderRadius: '10px',
                  transition: 'background 0.3s'
                },
                '&:hover::-webkit-scrollbar-thumb': { background: '#3B207F' }
              } 
            }}
          >
            <Typography variant="overline" sx={{ px: 2, py: 1, color: '#9aa0a6', fontWeight: 800, fontSize: '0.65rem' }}>
              {funnelFilter && funnelFilter !== 'all' ? `Etapas de ${stages.find(s => s.funnel_id === funnelFilter)?.funnel_name || 'Embudo'}` : 'Todas las etapas disponibles'}
            </Typography>
            <MenuItem onClick={() => { setStageFilter(null); setFilterAnchorEl(null); }} selected={stageFilter === null} sx={{ py: 1.2, mx: 1, borderRadius: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>Todas las etapas</Typography>
            </MenuItem>
            
            {funnelFilter && funnelFilter !== 'all' ? (
              stages.filter(s => s.funnel_id === funnelFilter).map(s => (
                <MenuItem key={s.id} onClick={() => { setStageFilter(s.id); setFilterAnchorEl(null); }} selected={stageFilter === s.id} sx={{ py: 1.2, mx: 1, borderRadius: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                </MenuItem>
              ))
            ) : (
              // Mostrar etapas únicas por nombre si estamos en "Todos los embudos"
              Array.from(new Map(stages.map(s => [s.name, s])).values()).map(s => (
                <MenuItem key={s.id} onClick={() => { setStageFilter(s.name); setFilterAnchorEl(null); }} selected={stageFilter === s.name} sx={{ py: 1.2, mx: 1, borderRadius: 2 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, mr: 1.5 }} />
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                </MenuItem>
              ))
            )}
          </Menu>

          {/* MENÚ FILTRO POR EMBUDO */}
          <Menu
            anchorEl={funnelFilterAnchorEl}
            open={Boolean(funnelFilterAnchorEl)}
            onClose={() => setFunnelFilterAnchorEl(null)}
            sx={{ zIndex: 110000 }}
            PaperProps={{
              sx: {
                borderRadius: 3, mt: 1,
                boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
                border: '1px solid #f0f2f5',
                minWidth: 260, zIndex: 110001,
                maxHeight: 420, overflowY: 'auto',
                '&::-webkit-scrollbar': { width: '3px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '10px' },
                '&:hover::-webkit-scrollbar-thumb': { background: '#3B207F' }
              }
            }}
          >
            {/* Encabezado con reset */}
            <Box sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="overline" sx={{ color: '#9aa0a6', fontWeight: 800, fontSize: '0.65rem' }}>Filtrar por Embudo</Typography>
              <Typography 
                variant="caption" 
                onClick={() => { setFunnelDialogOpen(true); setFunnelFilterAnchorEl(null); }}
                sx={{ color: '#3B207F', fontWeight: 800, cursor: 'pointer', fontSize: '0.75rem', textTransform: 'uppercase' }}
              >
                + Crear
              </Typography>
            </Box>

            <MenuItem
              onClick={() => { 
                setFunnelFilter('all'); 
                setStageFilter(null); 
                localStorage.setItem('messenger_active_funnel', 'all');
                setFunnelFilterAnchorEl(null);
              }}
              selected={funnelFilter === 'all'}
              sx={{ mx: 1, borderRadius: 2, py: 1.2, '&.Mui-selected': { bgcolor: '#3B207F10' } }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: funnelFilter === 'all' ? '#3B207F15' : '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <AccountTreeIcon sx={{ fontSize: 16, color: funnelFilter === 'all' ? '#3B207F' : '#9aa0a6' }} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>Todos los embudos</Typography>
                {funnelFilter === 'all' && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3B207F' }} />}
              </Box>
            </MenuItem>

            {/* Lista de embudos únicos */}
            {Array.from(new Map(stages.map(s => [s.funnel_id, s.funnel_name])).entries()).map(([fId, fName]) => (
              <MenuItem
                key={fId}
                onClick={() => { 
                  setFunnelFilter(fId); 
                  setStageFilter(null); 
                  localStorage.setItem('messenger_active_funnel', fId);
                  setFunnelFilterAnchorEl(null);
                }}
                selected={funnelFilter === fId}
                sx={{ mx: 1, borderRadius: 2, py: 1.2, '&.Mui-selected': { bgcolor: '#3B207F10' } }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: '8px', bgcolor: funnelFilter === fId ? '#3B207F15' : '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <AccountTreeIcon sx={{ fontSize: 16, color: funnelFilter === fId ? '#3B207F' : '#9aa0a6' }} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 700, flex: 1 }}>{fName || `Embudo ${fId}`}</Typography>
                  {funnelFilter === fId && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#3B207F' }} />}
                </Box>
              </MenuItem>
            ))}

            <Box sx={{ p: 1.5, pt: 1 }}>
              <Button 
                fullWidth size="small" variant="contained"
                onClick={() => setFunnelFilterAnchorEl(null)}
                sx={{ bgcolor: '#3B207F', borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: '0.8rem' }}
              >
                Aplicar filtro
              </Button>
            </Box>
          </Menu>
        </Box>

        {/* Tabs for quick switching */}
        <Box sx={{ px: 2, py: 1, display: 'flex', gap: 1, bgcolor: '#fff' }}>
           <Chip label="Bandeja de Entrada" size="small" onClick={() => setIsArchivedView(false)} variant={!isArchivedView ? "filled" : "outlined"} sx={{ fontWeight: 700, borderRadius: '8px', cursor: 'pointer', bgcolor: !isArchivedView ? '#3B207F' : 'transparent', color: !isArchivedView ? '#fff' : '#5f6368' }} />
           <Chip label="Archivados" size="small" onClick={() => setIsArchivedView(true)} variant={isArchivedView ? "filled" : "outlined"} sx={{ fontWeight: 700, borderRadius: '8px', cursor: 'pointer', bgcolor: isArchivedView ? '#5f6368' : 'transparent', color: isArchivedView ? '#fff' : '#5f6368' }} />
        </Box>

        {/* Search */}
        <Box sx={{ p: 2, bgcolor: '#fff' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: '#f1f3f4', borderRadius: '12px', px: 2, py: 1, transition: 'all 0.2s', '&:focus-within': { bgcolor: '#fff', boxShadow: '0 0 0 2px #3B207F40' } }}>
            <SearchIcon sx={{ color: '#5f6368', mr: 1.5, fontSize: 20 }} />
            <InputBase placeholder="Buscar conversación..." sx={{ flex: 1, fontSize: '0.875rem', fontWeight: 500 }} />
          </Box>
        </Box>

        {/* Chats List */}
        <Box sx={{ flex: 1, overflowY: 'auto', px: 1 }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}><CircularProgress size={30} sx={{ color: '#3B207F' }} /></Box>
          ) : conversations.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
              <Box sx={{ width: 60, height: 60, borderRadius: '50%', bgcolor: '#f8f9fa', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <AccountCircleIcon sx={{ fontSize: 32, color: '#dadce0' }} />
              </Box>
              <Typography variant="body2" sx={{ color: '#70757a', fontWeight: 500 }}>No hay conversaciones {isArchivedView ? 'archivadas' : 'activas'}</Typography>
            </Box>
          ) : (
            conversations
              .filter(c => {
                const passFunnel = !funnelFilter || funnelFilter === 'all' || !c.funnel_id || c.funnel_id == funnelFilter;
                const passStage = !stageFilter || (typeof stageFilter === 'string' ? c.stage_name === stageFilter : c.funnel_stage_id == stageFilter);
                return passFunnel && passStage;
              })
              .map(chat => (
              <Box 
                key={chat.id} 
                onClick={() => setSelectedChat(chat)}
                sx={{ 
                  display: 'flex', p: 2, mx: 1, my: 0.5, borderRadius: '16px', cursor: 'pointer',
                  bgcolor: selectedChat?.id === chat.id ? '#3B207F10' : 'transparent',
                  border: '1px solid',
                  borderColor: selectedChat?.id === chat.id ? '#3B207F20' : 'transparent',
                  '&:hover': { bgcolor: selectedChat?.id === chat.id ? '#3B207F15' : '#f8f9fa' },
                  transition: 'all 0.2s ease'
                }}
              >
                <Badge 
                  overlap="circular" 
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} 
                  variant="dot" 
                  color={chat.other_user?.is_online ? "success" : "transparent"}
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: chat.other_user?.is_online ? '#44b700' : 'transparent',
                      color: '#44b700',
                      boxShadow: `0 0 0 2px #fff`,
                      '&::after': chat.other_user?.is_online ? {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        animation: 'ripple 1.2s infinite ease-in-out',
                        border: '1px solid currentColor',
                        content: '""',
                      } : {},
                    },
                    '@keyframes ripple': {
                      '0%': { transform: 'scale(.8)', opacity: 1 },
                      '100%': { transform: 'scale(2.4)', opacity: 0 },
                    },
                  }}
                >
                  <Avatar src={chat.other_user?.avatar} sx={{ width: 48, height: 48, boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
                    {chat.other_user?.name?.charAt(0)}
                  </Avatar>
                </Badge>
                <Box sx={{ ml: 2, flex: 1, minWidth: 0 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
                    <Typography variant="subtitle2" noWrap sx={{ fontWeight: chat.unread_count > 0 ? 800 : 600, color: '#1a1a1a', fontSize: '0.9rem' }}>
                      {chat.other_user?.name}
                    </Typography>
                    <Typography variant="caption" sx={{ color: chat.unread_count > 0 ? '#3B207F' : '#70757a', fontWeight: 600, fontSize: '0.7rem' }}>
                      {chat.last_message_time ? new Date(chat.last_message_time).toLocaleDateString([], { month: 'short', day: 'numeric' }) : ''}
                    </Typography>
                  </Box>
                  <Typography variant="body2" noWrap sx={{ fontSize: '0.8rem', color: chat.unread_count > 0 ? '#1a1a1a' : '#5f6368', fontWeight: chat.unread_count > 0 ? 600 : 400, mb: 1 }}>
                    {(() => {
                      const shared = isPostPayload(chat.last_message);
                      if (shared) return "Compartió una publicación";
                      return chat.last_message ? chat.last_message.replace(/<[^>]*>?/gm, '') : 'Inicia una conversación';
                    })()}
                  </Typography>
                  
                  {/* BADGES REORGANIZADOS */}
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
                    {/* Fila 1: Origen y Etapa */}
                    <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                      <Box sx={{ 
                        display: 'flex', alignItems: 'center', 
                        bgcolor: `${originColors[chat.project_source] || '#888'}15`, 
                        color: originColors[chat.project_source] || '#888', 
                        px: 1, py: 0.3, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 800, gap: 0.5,
                        textTransform: 'uppercase', letterSpacing: 0.5
                      }}>
                        {originIcons[chat.project_source] || <ComputerIcon sx={{ fontSize: 12 }} />}
                        {chat.project_source}
                      </Box>
                      {chat.stage_name && (
                        <Box sx={{ 
                          px: 1, py: 0.3, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 800,
                          bgcolor: `${chat.stage_color || '#888'}15`, color: chat.stage_color || '#888',
                          textTransform: 'uppercase', letterSpacing: 0.5
                        }}>
                          {chat.stage_name}
                        </Box>
                      )}
                    </Box>

                    {/* Fila 2: Etiquetas personalizadas */}
                    {chat.tags && chat.tags.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        {chat.tags.slice(0, 3).map(tag => (
                          <Box key={tag.id} sx={{ 
                            px: 1, py: 0.3, borderRadius: '4px', fontSize: '0.6rem', fontWeight: 800,
                            bgcolor: `${tag.color}15`, color: tag.color,
                            textTransform: 'uppercase', letterSpacing: 0.5
                          }}>
                            {tag.name}
                          </Box>
                        ))}
                        {chat.tags.length > 3 && (
                          <Typography variant="caption" sx={{ fontSize: '0.6rem', fontWeight: 800, color: '#888' }}>
                            +{chat.tags.length - 3}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                </Box>
                <IconButton size="small" onClick={(e) => handleOpenChatMenu(e, chat)} sx={{ ml: 1, color: '#9aa0a6', alignSelf: 'flex-start', mt: 0.5 }}>
                  <MoreVertIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Box>
            ))
          )}
        </Box>
        <Menu
          anchorEl={chatMenuAnchor}
          open={Boolean(chatMenuAnchor)}
          onClose={() => setChatMenuAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          sx={{ zIndex: 999999 }}
          PaperProps={{ sx: { borderRadius: 3, minWidth: 160, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}
        >
          <MenuItem onClick={openDeleteConfirm} sx={{ py: 1.2, color: '#d32f2f', fontWeight: 600 }}>
            <DeleteIcon sx={{ fontSize: 18, mr: 1.5 }} /> Eliminar conversación
          </MenuItem>
        </Menu>

        {/* Confirm Dialog */}
        <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
          sx={{ zIndex: 999999 }}
          PaperProps={{ sx: { borderRadius: 4, p: 1, maxWidth: 400 } }}
        >
          <DialogTitle sx={{ fontWeight: 800, fontSize: '1.1rem', pb: 0 }}>{confirmDialog.title}</DialogTitle>
          <DialogContent sx={{ pt: 2, pb: 1 }}>
            <Typography variant="body2" color="text.secondary">{confirmDialog.message}</Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConfirmDialog(prev => ({ ...prev, open: false }))}
              sx={{ textTransform: 'none', fontWeight: 600, color: '#5f6368', borderRadius: 2 }}
            >
              Cancelar
            </Button>
            <Button onClick={confirmDialog.onConfirm} variant="contained" color="error"
              sx={{ textTransform: 'none', fontWeight: 700, borderRadius: 2, boxShadow: 'none' }}
            >
              Eliminar
            </Button>
          </DialogActions>
        </Dialog>
      </Box>

      {/* RIGHT CHAT AREA */}
      <Box sx={{ flex: 1, height: '100%', display: { xs: selectedChat ? 'flex' : 'none', md: 'flex' }, flexDirection: 'column', bgcolor: '#fff', position: 'relative' }}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <Box sx={{ p: 2, bgcolor: '#fff', display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f2f5', justifyContent: 'space-between', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.02)' }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <IconButton 
                  sx={{ display: { xs: 'flex', md: 'none' }, mr: 1, bgcolor: '#f1f3f4' }} 
                  onClick={() => {
                    setSelectedChat(null);
                    if (activeContext) {
                      localStorage.removeItem(`messenger_selected_chat_id_${activeContext.id}`);
                    }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
                <Badge 
                  overlap="circular" 
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} 
                  variant="dot" 
                  sx={{
                    '& .MuiBadge-badge': {
                      backgroundColor: (conversations.find(c => c.id === selectedChat.id)?.other_user?.is_online || selectedChat.other_user?.is_online) ? '#44b700' : 'transparent',
                      color: '#44b700',
                      boxShadow: `0 0 0 2px #fff`,
                      '&::after': (conversations.find(c => c.id === selectedChat.id)?.other_user?.is_online || selectedChat.other_user?.is_online) ? {
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        animation: 'ripple 1.2s infinite ease-in-out',
                        border: '1px solid currentColor',
                        content: '""',
                      } : {},
                    },
                  }}
                >
                  <Avatar src={selectedChat.other_user?.avatar} sx={{ width: 40, height: 40, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>{selectedChat.other_user?.name?.charAt(0)}</Avatar>
                </Badge>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a1a', lineHeight: 1.2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    {selectedChat.other_user?.name}
                    {(conversations.find(c => c.id === selectedChat.id)?.other_user?.is_online || selectedChat.other_user?.is_online) && (
                      <Typography variant="caption" sx={{ color: '#44b700', fontWeight: 800, fontSize: '0.6rem', textTransform: 'uppercase' }}>• En línea</Typography>
                    )}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, position: 'relative', zIndex: 20 }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: otherUserTyping ? '#3B207F' : '#70757a', 
                        fontWeight: otherUserTyping ? 800 : 600,
                        fontStyle: otherUserTyping ? 'italic' : 'normal'
                      }}
                    >
                      {otherUserTyping ? 'escribiendo...' : (selectedChat.other_user?.sub || (selectedChat.other_user?.type === 'enterprise' ? 'Empresa' : 'Profesional'))}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      onClick={(e) => {
                      setChatStageAnchorEl(e.currentTarget);
                    }}
                      sx={{ 
                        color: selectedChat.stage_color || '#3B207F', 
                        fontWeight: 800, 
                        textTransform: 'uppercase', 
                        fontSize: '0.7rem',
                        cursor: 'pointer',
                        bgcolor: `${selectedChat.stage_color || '#3B207F'}15`,
                        px: 1, py: 0.2, borderRadius: '4px',
                        display: 'inline-block',
                        '&:hover': { bgcolor: `${selectedChat.stage_color || '#3B207F'}25` }
                      }}
                    >
                      {selectedChat.stage_name || 'Sin etapa'}
                    </Typography>
                    <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: '#dadce0' }} />
                    <Typography variant="caption" sx={{ color: '#70757a', fontWeight: 500 }}>{selectedChat.other_user?.type === 'enterprise' ? 'Empresa' : 'Profesional'}</Typography>
                  </Box>
                </Box>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>

                <Tooltip title="Detalles y CRM">
                  <IconButton onClick={() => {
                    setCrmOpen(!crmOpen);
                  }} sx={{ 
                    bgcolor: crmOpen ? '#3B207F10' : 'transparent', 
                    color: crmOpen ? '#3B207F' : '#5f6368',
                    zIndex: 25,
                    '&:hover': { bgcolor: '#f1f3f4' }
                  }}>
                    <LocalOfferIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Messages Area */}
            <Box 
              ref={scrollContainerRef}
              onScroll={(e) => {
                const el = e.currentTarget;
                const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 120;
                userScrolledUpRef.current = !nearBottom;
              }}
              sx={{ 
                flex: 1, 
                overflowY: 'auto', 
                p: 3, 
                display: 'flex', 
                flexDirection: 'column', 
                gap: 2, 
                bgcolor: '#f8f9fa' 
              }}
            >
              {messages.length === 0 ? (
                <Box sx={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', opacity: 0.5 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>No hay mensajes aún</Typography>
                  <Typography variant="caption">Envía un mensaje para comenzar la conversación</Typography>
                </Box>
              ) : messages.map((msg, idx) => {
                const isMe = msg.sender_id == activeContext?.id;
                const showAvatar = idx === 0 || messages[idx-1].sender_id != msg.sender_id;
                
                return (
                  <Box key={msg.id} sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: isMe ? 'flex-end' : 'flex-start',
                  }}>
                    <Box sx={{ 
                      bgcolor: isMe ? '#3B207F' : '#fff', 
                      color: isMe ? '#fff' : '#1a1a1a', 
                      px: isPostPayload(msg.message_text) ? 0.6 : 2, py: isPostPayload(msg.message_text) ? 0.6 : 1.2, 
                      borderRadius: isMe ? '18px 18px 2px 18px' : '18px 18px 18px 2px', 
                      maxWidth: '80%', 
                      boxShadow: isMe ? '0 4px 12px rgba(59, 32, 127, 0.2)' : '0 2px 8px rgba(0,0,0,0.05)',
                      position: 'relative',
                      border: isMe ? 'none' : '1px solid #f0f2f5',
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere'
                    }}>
                      {isPostPayload(msg.message_text) ? (
                        <Box>
                          <SharedPostCard text={msg.message_text} isMe={isMe} />
                          <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.7, fontSize: '0.65rem' }}>
                            {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                          </Typography>
                        </Box>
                      ) : (
                      <>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontSize: '0.9rem', 
                          lineHeight: 1.4, 
                          '& p': { m: 0 },
                          '& a': { 
                            color: isMe ? '#fff' : '#3B207F', 
                            fontWeight: 700, 
                            textDecoration: 'underline',
                            '&:hover': { opacity: 0.8 }
                          } 
                        }} 
                        dangerouslySetInnerHTML={{ 
                          __html: (() => {
                            let text = msg.message_text || '';
                            const imgExtRe = /\.(jpg|jpeg|png|gif|webp|bmp|svg)(\?[^\s]*)?$/i;
                            const lines = text.split('\n');
                            const formatted = lines.map(line => {
                              const trimmed = line.trim();
                              if (imgExtRe.test(trimmed) && /^https?:\/\//i.test(trimmed)) {
                                return `<img src="${trimmed}" alt="imagen" style="max-width:100%;max-height:300px;border-radius:8px;display:block;margin:4px 0" />`;
                              }
                              return trimmed.replace(
                                /(<img[^>]*>|<a[^>]*>.*?<\/a>)|((https?:\/\/|www\.)[^\s<]+)/g,
                                (match, tag, p1) => {
                                  if (tag) return tag;
                                  if (p1) {
                                    const href = p1.startsWith('http') ? p1 : `https://${p1}`;
                                    const isInternal = href.startsWith(window.location.origin) || href.startsWith('/');
                                    return `<a href="${href}"${isInternal ? '' : ' target="_blank" rel="noopener noreferrer"'}>${p1}</a>`;
                                  }
                                  return match;
                                }
                              );
                            });
                            return formatted.join('<br>');
                          })()
                        }} 
                      />
                      <Typography variant="caption" sx={{ display: 'block', textAlign: 'right', mt: 0.5, opacity: 0.7, fontSize: '0.65rem' }}>
                        {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </Typography>
                      </>
                      )}
                    </Box>
                  </Box>
                );
              })}
              <div ref={messagesEndRef} />
            </Box>

            {/* Input Area con Editor Avanzado */}
            <Box sx={{ p: 2, bgcolor: '#fff', borderTop: '1px solid #f0f2f5', position: 'relative' }}>
              {emojiPickerOpen && (
                <Box ref={emojiRef} sx={{ position: 'absolute', bottom: '100%', left: 16, zIndex: 10005, mb: 1 }}>
                  <EmojiPicker 
                    emojiStyle={EmojiStyle.APPLE} 
                    onEmojiClick={handleEmojiClick}
                    language="es"
                    searchPlaceholder="Buscar emoji..."
                    categories={[
                      { category: 'suggested', name: 'Recientes' },
                      { category: 'smileys_people', name: 'Caras y Personas' },
                      { category: 'animals_nature', name: 'Animales y Naturaleza' },
                      { category: 'food_drink', name: 'Comida y Bebida' },
                      { category: 'travel_places', name: 'Viajes y Lugares' },
                      { category: 'activities', name: 'Actividades' },
                      { category: 'objects', name: 'Objetos' },
                      { category: 'symbols', name: 'Símbolos' },
                      { category: 'flags', name: 'Banderas' }
                    ]}
                    previewConfig={{
                      showPreview: false
                    }}
                  />
                </Box>
              )}

              <Box sx={{ 
                bgcolor: '#f8f9fa', 
                borderRadius: '16px', 
                border: '1px solid #eef0f2',
                transition: 'all 0.2s ease',
                '&:focus-within': { borderColor: '#3B207F', bgcolor: '#fff', boxShadow: '0 4px 12px rgba(59, 32, 127, 0.05)' },
                overflow: 'hidden'
              }}>
                <style>{`
                  .quill-chat-editor .ql-container.ql-snow { border: none; font-family: inherit; font-size: 0.9rem; }
                  .quill-chat-editor .ql-editor { min-height: 44px; max-height: 180px; padding: 12px 16px; }
                  .quill-chat-editor .ql-editor.ql-blank::before { left: 16px; font-style: normal; color: #9aa0a6; }
                  .quill-chat-editor .ql-toolbar.ql-snow { border: none; border-top: 1px solid #f0f2f5; padding: 6px 12px; background: #fff; }
                  .quill-chat-editor .ql-formats { margin-right: 12px !important; }
                  .quill-chat-editor .ql-snow.ql-toolbar button:hover, .quill-chat-editor .ql-snow .ql-toolbar button:hover { color: #3B207F; }
                `}</style>
                
                <ReactQuill
                  ref={(el) => setQuillRef(el)}
                  theme="snow"
                  value={newMessage}
                  onChange={onEditorChange}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  placeholder="Escribe un mensaje profesional..."
                  className="quill-chat-editor"
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ 'list': 'ordered'}, { 'list': 'bullet' }]
                    ]
                  }}
                />

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, px: 2, bgcolor: '#fff', borderTop: '1px solid #f0f2f5' }}>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton size="small" sx={{ color: '#5f6368', '&:hover': { color: '#3B207F', bgcolor: '#3B207F05' } }}>
                      <AttachFileIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      onClick={() => setEmojiPickerOpen(!emojiPickerOpen)}
                      sx={{ 
                        color: emojiPickerOpen ? '#3B207F' : '#5f6368',
                        bgcolor: emojiPickerOpen ? '#3B207F05' : 'transparent',
                        '&:hover': { color: '#3B207F', bgcolor: '#3B207F05' }
                      }}
                    >
                      <InsertEmoticonIcon fontSize="small" />
                    </IconButton>
                  </Box>

                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <Button 
                      variant="contained" 
                      onClick={handleSendMessage}
                      disabled={!newMessage.replace(/<[^>]*>?/gm, '').trim()}
                      sx={{ 
                        bgcolor: '#3B207F', 
                        borderRadius: '12px', 
                        px: 3, 
                        textTransform: 'none', 
                        fontWeight: 700,
                        boxShadow: '0 4px 12px rgba(59, 32, 127, 0.2)',
                        '&:hover': { bgcolor: '#2a165f' }
                      }}
                      endIcon={<SendIcon />}
                    >
                      Enviar
                    </Button>
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* Panel Lateral CRM (Drawer) - PREMIUM DESIGN */}
            <Drawer
              anchor="right"
              open={crmOpen}
              onClose={() => setCrmOpen(false)}
              variant="temporary"
              ModalProps={{ keepMounted: true }}
              PaperProps={{
                sx: { 
                  width: { xs: '100%', sm: 380 }, 
                  height: 'calc(100% - 65px)', 
                  top: '65px',
                  borderLeft: '1px solid #f0f2f5',
                  boxShadow: '-10px 0 50px rgba(0,0,0,0.15)',
                  bgcolor: '#fff'
                }
              }}
              sx={{ 
                zIndex: 99999, // Justo debajo del header de holding (100000)
                '& .MuiBackdrop-root': { top: '65px' } // El backdrop también empieza abajo
              }}
            >
              <Box sx={{ 
                p: 0, 
                height: '100%', 
                display: 'flex', 
                flexDirection: 'column',
                overflowY: 'auto',
                overflowX: 'hidden',
                '&::-webkit-scrollbar': { width: '3px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { 
                  background: 'transparent', 
                  borderRadius: '10px',
                  transition: 'background 0.3s'
                },
                '&:hover::-webkit-scrollbar-thumb': { background: '#3B207F' }
              }}>
                {/* Header Estilo WhatsApp */}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 3,
                  p: 2, 
                  bgcolor: '#fff',
                  position: 'sticky',
                  top: 0,
                  zIndex: 10
                }}>
                  <IconButton onClick={() => setCrmOpen(false)} size="small" sx={{ color: '#5f6368' }}>
                    <CloseIcon />
                  </IconButton>
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, color: '#1a1a1a' }}>Info. del contacto</Typography>
                </Box>

                {/* HERO SECTION - WHATSAPP STYLE */}
                <Box sx={{ position: 'relative', mb: 1 }}>
                  {/* Banner */}
                  <Box sx={{ 
                    height: 180, 
                    backgroundImage: selectedChat?.other_user?.banner ? `url(${selectedChat.other_user.banner})` : 'none',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    bgcolor: '#3B207F',
                  }} />
                  
                  {/* Avatar Superpuesto */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    mt: -8, // Solapamiento
                    position: 'relative',
                    zIndex: 2
                  }}>
                    <Avatar 
                      src={selectedChat?.other_user?.avatar} 
                      sx={{ 
                        width: 160, height: 160, 
                        border: '5px solid #fff', 
                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                        bgcolor: '#f0f2f5'
                      }}
                    >
                      {selectedChat?.other_user?.name?.charAt(0)}
                    </Avatar>

                    <Box sx={{ textAlign: 'center', mt: 2, px: 3 }}>
                      <Typography variant="h5" sx={{ fontWeight: 600, color: '#1a1a1a', mb: 0.5 }}>
                        {selectedChat?.other_user?.name}
                      </Typography>
                      <Typography variant="body1" sx={{ color: '#70757a', mb: 0.5 }}>
                        {selectedChat?.other_user?.sub || (selectedChat?.other_user?.type === 'enterprise' ? 'Empresa' : 'Profesional')}
                      </Typography>
                      <Box sx={{ mt: 1, display: 'flex', justifyContent: 'center', gap: 1 }}>
                         <Chip label={selectedChat?.project_source} size="small" sx={{ fontWeight: 700, bgcolor: '#f0f2f5', color: originColors[selectedChat?.project_source] }} />
                      </Box>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ p: 3, pt: 0 }}>
                  {/* DETALLES DEL PERFIL */}
                  <Box sx={{ mb: 4, p: 2.5, bgcolor: '#f8f9fa', borderRadius: '20px', border: '1px solid #f0f2f5' }}>
                    <Typography variant="caption" sx={{ fontWeight: 800, color: '#70757a', textTransform: 'uppercase', mb: 2, display: 'block' }}>Información del Perfil</Typography>
                    
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      {selectedChat?.other_user?.bio && (
                        <Box>
                          <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>Descripción</Typography>
                          <Typography variant="body2" sx={{ color: '#444', fontSize: '0.85rem', lineHeight: 1.5 }}>{selectedChat.other_user.bio}</Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                        {selectedChat?.other_user?.location && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>Ubicación</Typography>
                            <Typography variant="body2" sx={{ color: '#444', fontSize: '0.8rem', fontWeight: 600 }}>{selectedChat.other_user.location}</Typography>
                          </Box>
                        )}
                        {selectedChat?.other_user?.email && (
                          <Box>
                            <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>Email</Typography>
                            <Typography variant="body2" sx={{ color: '#444', fontSize: '0.75rem', fontWeight: 600, wordBreak: 'break-all' }}>{selectedChat.other_user.email}</Typography>
                          </Box>
                        )}
                        {selectedChat?.other_user?.web && (
                          <Box sx={{ gridColumn: 'span 2' }}>
                            <Typography variant="caption" sx={{ color: '#aaa', fontWeight: 600 }}>Sitio Web</Typography>
                            <Typography variant="body2" sx={{ color: '#3B207F', fontSize: '0.8rem', fontWeight: 700 }}>{selectedChat.other_user.web}</Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </Box>

                {/* Gestión CRM */}
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: '#3B207F', borderRadius: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Embudo y Estado</Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    {/* Selector de Embudo */}
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label="Seleccionar Embudo"
value={selectedFunnelId || ''}
                       onChange={(e) => handleUpdateFunnel(e.target.value)}
                           SelectProps={{
                            onOpen: async () => {
                              if (stagesOpeningRef.current) return;
                              stagesOpeningRef.current = true;
                              try {
                                const resF = await axios.post(API_BASE, { accion: 'getFunnelStages' });
                                if (resF.data.success && resF.data.data.length) setStages(resF.data.data);
                              } catch (e) { }
                              finally { setTimeout(() => { stagesOpeningRef.current = false; }, 500); }
                            },
                           MenuProps: {
                             disablePortal: true,
                             sx: { zIndex: 11001 }
                           }
                         }}
                         sx={{
                        '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8f9fa', fontWeight: 600 },
                        '& .MuiSelect-select': { py: 1.2, fontSize: '0.85rem' }
                      }}
                    >
                      {/* Generar lista única de embudos desde las etapas */}
                      {Array.from(new Map(stages.map(s => [s.funnel_id, s.funnel_name])).entries()).map(([fId, fName]) => (
                        <MenuItem key={fId} value={fId}>
                          {fName || `Embudo ${fId}`}
                        </MenuItem>
                      ))}
                    </TextField>

                    {/* Selector de Etapa */}
                    {isCreatingStage ? (
                      <TextField
                        fullWidth
                        autoFocus
                        size="small"
                        label="Nombre de nueva etapa"
                        value={tempStageName}
                        onChange={(e) => setTempStageName(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleSaveNewStage()}
                        InputProps={{
                          endAdornment: (
                            <IconButton size="small" onClick={handleSaveNewStage} sx={{ color: '#3B207F' }}>
                              <CheckIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                          )
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff' }
                        }}
                      />
                    ) : (
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Etapa Actual"
                        value={stages.find(s => s.id == selectedChat?.funnel_stage_id && s.funnel_id == selectedFunnelId) ? selectedChat.funnel_stage_id : ''}
                        onChange={(e) => {
                          if (e.target.value === 'NEW') {
                            setIsCreatingStage(true);
                          } else {
                            handleUpdateStage(e.target.value);
                          }
                        }}
                        SelectProps={{
                          displayEmpty: true,
                          renderValue: (selected) => {
                            if (!selected) return <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic' }}>Sin etapa o etapa de otro embudo</Typography>;
                            const stage = stages.find(s => s.id == selected);
                            if (!stage) return null;
                            return (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: stage.color }} />
                                {stage.name}
                              </Box>
                            );
                          },
                          MenuProps: { 
                            disablePortal: true,
                            sx: { zIndex: 11001 }
                          }
                        }}
                        sx={{ 
                          '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#fff', fontWeight: 600 },
                          '& .MuiSelect-select': { py: 1.2, fontSize: '0.85rem' }
                        }}
                      >
                        <MenuItem value="NEW" sx={{ color: '#3B207F', fontWeight: 800, borderBottom: '1px dashed #3B207F20' }}>
                          + CREAR NUEVA ETAPA
                        </MenuItem>
                        {stages.filter(s => s.funnel_id === selectedFunnelId).map((s) => (
                          <MenuItem key={s.id} value={s.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: s.color }} />
                              {s.name}
                            </Box>
                            <IconButton size="small" onClick={(e) => handleDeleteStage(e, s.id)} sx={{ color: '#ff4d4d', p: 0.5 }}>
                              <DeleteIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  </Box>
                </Box>

                <Box sx={{ mb: 4 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: '#3B207F', borderRadius: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Etiquetas del Cliente</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {selectedChat?.tags?.map((tag) => (
                      <Chip 
                        key={tag.id} 
                        label={tag.name} 
                        size="small" 
                        onDelete={() => handleRemoveTag(tag.id)}
                        sx={{ 
                          bgcolor: `${tag.color}15`, color: tag.color, fontWeight: 700, borderRadius: '8px',
                          border: `1px solid ${tag.color}30`,
                          '& .MuiChip-deleteIcon': { color: tag.color, fontSize: 16, '&:hover': { color: tag.color } }
                        }} 
                      />
                    ))}
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    <TextField 
                      placeholder="Escribe o selecciona etiqueta..." 
                      size="small" 
                      fullWidth 
                      value={newTagInput}
                      onChange={(e) => {
                        e.stopPropagation();
                        setNewTagInput(e.target.value);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTag();
                        }
                      }}
                      inputProps={{
                        list: "existing-tags"
                      }}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: '#f8f9fa' } }}
                    />
                    <datalist id="existing-tags">
                      {[...new Set(conversations.flatMap(c => c.tags || []).map(t => t.name))].map(tagName => (
                        <option key={tagName} value={tagName} />
                      ))}
                    </datalist>
                    <IconButton onClick={handleAddTag} sx={{ bgcolor: '#3B207F', color: '#fff', borderRadius: '12px', '&:hover': { bgcolor: '#2a165f' } }}>
                      <SendIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box sx={{ mt: 'auto', pt: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Button 
                    fullWidth 
                    variant="contained" 
                    onClick={() => setCrmOpen(false)}
                    sx={{ 
                      bgcolor: '#3B207F', py: 1.5, borderRadius: '16px', fontWeight: 800, 
                      boxShadow: '0 8px 24px rgba(59, 32, 127, 0.2)',
                      '&:hover': { bgcolor: '#2a165f', transform: 'translateY(-2px)' },
                      transition: 'all 0.2s'
                    }}
                  >
                    Guardar Cambios
                  </Button>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    color={isArchivedView ? "primary" : "inherit"}
                    onClick={handleArchive}
                    startIcon={isArchivedView ? null : null}
                    sx={{ 
                      py: 1.5, borderRadius: '16px', fontWeight: 700, color: '#70757a', borderColor: '#dadce0',
                      '&:hover': { bgcolor: '#f8f9fa', borderColor: '#70757a' }
                    }}
                  >
                    {isArchivedView ? 'Desarchivar Conversación' : 'Archivar Conversación'}
                  </Button>
                </Box>

                {/* SECCIÓN DE NOTAS */}
                <Box sx={{ p: 3, pt: 0, mt: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: '#3B207F', borderRadius: 1 }} />
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#1a1a1a', textTransform: 'uppercase', fontSize: '0.75rem', letterSpacing: 0.5 }}>Notas del Contacto</Typography>
                  </Box>

                  <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      placeholder="Escribe una nota interna sobre este lead..."
                      value={newNoteInput}
                      onChange={(e) => setNewNoteInput(e.target.value)}
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: '16px', bgcolor: '#f8f9fa', fontSize: '0.85rem' } }}
                    />
                    <Button 
                      onClick={handleAddNote} 
                      disabled={!newNoteInput.trim()}
                      variant="outlined" 
                      size="small"
                      sx={{ alignSelf: 'flex-end', borderRadius: '10px', textTransform: 'none', fontWeight: 700 }}
                    >
                      Añadir Nota
                    </Button>
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
                    {notes.map((note) => (
                      <Box key={note.id} sx={{ p: 2, bgcolor: '#fff6e5', borderRadius: '16px', border: '1px solid #ffecb3' }}>
                        <Typography variant="body2" sx={{ color: '#5d4037', fontSize: '0.85rem', whiteSpace: 'pre-wrap' }}>
                          {note.content}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, alignItems: 'center' }}>
                          <Typography variant="caption" sx={{ color: '#8d6e63', fontWeight: 600 }}>{note.user_name}</Typography>
                          <Typography variant="caption" sx={{ color: '#a1887f' }}>{new Date(note.created_at).toLocaleDateString()}</Typography>
                        </Box>
                      </Box>
                    ))}
                    {notes.length === 0 && (
                      <Typography variant="body2" sx={{ color: '#aaa', fontStyle: 'italic', textAlign: 'center', py: 2 }}>Sin notas todavía</Typography>
                    )}
                  </Box>
                </Box>
                </Box>
              </Box>
            </Drawer>
          </>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', bgcolor: '#f8f9fa', px: 4 }}>
            <Box sx={{ p: 4, borderRadius: '50%', bgcolor: '#fff', boxShadow: '0 20px 40px rgba(0,0,0,0.05)', mb: 4 }}>
              <InsertEmoticonIcon sx={{ fontSize: 60, color: '#3B207F', opacity: 0.2 }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 800, color: '#1a1a1a', mb: 1 }}>Centro de Mensajería</Typography>
            <Typography variant="body1" sx={{ color: '#70757a', textAlign: 'center', maxWidth: 400 }}>Gestiona tus leads y clientes desde una sola interfaz profesional.</Typography>
          </Box>
        )}
      </Box>

      {/* POPUP GRANDE PARA CREAR EMBUDO */}
      <Dialog 
        open={funnelDialogOpen} 
        onClose={() => setFunnelDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        disablePortal
        disableEnforceFocus
        disableAutoFocus
        sx={{ zIndex: 10000 }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Crear Nuevo Embudo de Mensajes</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 3, color: '#70757a' }}>
            Define un nombre para tu flujo y las etapas por las que pasarán tus leads.
          </Typography>
          <TextField 
            fullWidth 
            autoFocus
            label="Nombre del Embudo" 
            placeholder="ej: Ventas Mayo, Seguimiento Bolsa..." 
            value={newFunnelName}
            onChange={(e) => {
              setNewFunnelName(e.target.value);
            }}
            sx={{ mb: 3 }}
          />
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Etapas del flujo:</Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {newFunnelStages.map((s, i) => (
              <Chip 
                key={i} 
                label={s} 
                onDelete={() => {
                  setNewFunnelStages(prev => prev.filter((_, idx) => idx !== i));
                }}
                sx={{ 
                  fontWeight: 600, 
                  bgcolor: '#f0f2f5',
                  '& .MuiChip-deleteIcon': { color: '#e11d48', fontSize: 18 }
                }} 
              />
            ))}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField 
              fullWidth 
              size="small"
              placeholder="Nueva etapa (ej: Seguimiento, Cierre...)" 
              value={dialogStageInput}
              onChange={(e) => setDialogStageInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (dialogStageInput.trim()) {
                    setNewFunnelStages(prev => [...prev, dialogStageInput.trim()]);
                    setDialogStageInput("");
                  }
                }
              }}
            />
            <Button 
              variant="outlined" 
              onClick={() => {
                if (dialogStageInput.trim()) {
                  setNewFunnelStages(prev => [...prev, dialogStageInput.trim()]);
                  setDialogStageInput("");
                }
              }}
              sx={{ borderRadius: '8px', textTransform: 'none', fontWeight: 700 }}
            >
              Añadir
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setFunnelDialogOpen(false)} sx={{ fontWeight: 700 }}>Cancelar</Button>
          <Button onClick={handleCreateFunnel} variant="contained" sx={{ bgcolor: '#3B207F', fontWeight: 800 }}>Crear Embudo Ahora</Button>
        </DialogActions>
      </Dialog>

      {/* MENÚ PARA CAMBIAR ETAPA DESDE EL HEADER DEL CHAT */}
      <Menu
        anchorEl={chatStageAnchorEl}
        open={Boolean(chatStageAnchorEl)}
        onClose={() => setChatStageAnchorEl(null)}
        sx={{ zIndex: 110000 }}
        PaperProps={{
          sx: {
            borderRadius: 3, mt: 1,
            boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
            border: '1px solid #f0f2f5',
            minWidth: 200, zIndex: 110001
          }
        }}
      >
        <Typography variant="overline" sx={{ px: 2, py: 1, color: '#9aa0a6', fontWeight: 800, fontSize: '0.65rem' }}>Cambiar Etapa</Typography>
        {stages
          .filter(s => s.funnel_id == selectedChat?.funnel_id)
          .map(s => (
            <MenuItem 
              key={s.id} 
              onClick={() => { 
                handleUpdateStage(s.id); 
                setChatStageAnchorEl(null); 
              }} 
              selected={selectedChat?.funnel_stage_id == s.id}
              sx={{ py: 1.2, mx: 1, borderRadius: 2 }}
            >
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: s.color, mr: 1.5 }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
            </MenuItem>
          ))
        }
        {stages.filter(s => s.funnel_id == selectedChat?.funnel_id).length === 0 && (
          <MenuItem disabled>
            <Typography variant="caption">No hay etapas en este embudo</Typography>
          </MenuItem>
        )}
      </Menu>

      {/* FEEDBACK SYSTEM */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity} 
          variant="filled"
          sx={{ width: '100%', borderRadius: '12px', fontWeight: 700 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}